/**
 * Centralized API client for making authenticated requests
 * Uses simple token-based authentication stored in localStorage
 *
 * TODO: Consider migrating to Cognito JWT tokens in production
 */

// Gate debug logging behind NODE_ENV for development use only
const debugLog = (...args: any[]) => {
  if (process.env.NODE_ENV === 'development') {
    console.log(...args);
  }
};

const API_BASE_URL = process.env.REACT_APP_API_URL || '';

debugLog('[DEBUG api] Module loaded');
debugLog('[DEBUG api] API_BASE_URL:', API_BASE_URL);

interface RequestOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'DELETE' | 'PATCH';
  headers?: Record<string, string>;
  body?: any;
  skipAuth?: boolean;
}

interface ApiError extends Error {
  status?: number;
  data?: any;
}

/**
 * Get the admin token from localStorage
 * Returns null if user is not authenticated
 */
const getAuthToken = (): string | null => {
  try {
    debugLog('[DEBUG api] Fetching auth token from localStorage...');
    const token = localStorage.getItem('spitfire_admin_token');

    if (token) {
      debugLog('[DEBUG api] Auth token retrieved successfully');
      return token;
    }

    debugLog('[DEBUG api] No admin token available in localStorage');
    return null;
  } catch (error) {
    console.error('[DEBUG api] Error fetching auth token:', error);
    return null;
  }
};

/**
 * Make an authenticated API request
 * Automatically attaches Authorization header with admin token
 *
 * @param endpoint - API endpoint (e.g., '/admin/reviews')
 * @param options - Request options (method, headers, body, skipAuth)
 * @returns Parsed JSON response
 *
 * @throws ApiError with status code and response data
 *
 * TODO: Add retry logic for transient failures
 * TODO: Add request caching for GET requests
 * TODO: Add request timeout configuration
 */
const apiRequest = async <T = any>(
  endpoint: string,
  options: RequestOptions = {}
): Promise<T> => {
  const {
    method = 'GET',
    headers = {},
    body,
    skipAuth = false,
  } = options;

  debugLog(`[DEBUG api] Making ${method} request to ${endpoint}`);

  // Build request URL
  const url = `${API_BASE_URL}${endpoint}`;

  // Build headers
  const requestHeaders: Record<string, string> = {
    'Content-Type': 'application/json',
    ...headers,
  };

  // Add authorization header if not skipped
  if (!skipAuth) {
    const token = getAuthToken();
    if (token) {
      requestHeaders['Authorization'] = `Bearer ${token}`;
      debugLog('[DEBUG api] Authorization header added');
    } else {
      debugLog('[DEBUG api] No auth token available, request may fail if auth is required');
    }
  }

  // Build request config
  const config: RequestInit = {
    method,
    headers: requestHeaders,
  };

  // Add body for non-GET requests
  if (body && method !== 'GET') {
    config.body = JSON.stringify(body);
    // SECURITY: Never log request bodies for authentication endpoints
    if (body && !endpoint.includes('/auth')) {
      debugLog('[DEBUG api] Request body:', body);
    }
  }

  try {
    debugLog('[DEBUG api] Sending request to:', url);
    const response = await fetch(url, config);

    debugLog(`[DEBUG api] Response status: ${response.status} ${response.statusText}`);

    // Parse response body
    let data: any;
    const contentType = response.headers.get('content-type');
    if (contentType?.includes('application/json')) {
      data = await response.json();
      // SECURITY: Never log auth response data
      if (!endpoint.includes('/auth')) {
        debugLog('[DEBUG api] Response data:', data);
      }
    } else {
      data = await response.text();
      debugLog('[DEBUG api] Response text:', data);
    }

    // Handle error responses
    if (!response.ok) {
      const error: ApiError = new Error(
        data.message || `HTTP ${response.status}: ${response.statusText}`
      );
      error.status = response.status;
      error.data = data;
      console.error('[DEBUG api] Request failed:', error);
      throw error;
    }

    debugLog('[DEBUG api] Request successful');
    return data;
  } catch (error) {
    console.error('[DEBUG api] Request error:', error);
    throw error;
  }
};

/**
 * Convenience method for GET requests
 */
export const get = <T = any>(
  endpoint: string,
  options?: Omit<RequestOptions, 'method' | 'body'>
): Promise<T> => {
  debugLog(`[DEBUG api] GET ${endpoint}`);
  return apiRequest<T>(endpoint, { ...options, method: 'GET' });
};

/**
 * Convenience method for POST requests
 */
export const post = <T = any>(
  endpoint: string,
  body?: any,
  options?: Omit<RequestOptions, 'method' | 'body'>
): Promise<T> => {
  debugLog(`[DEBUG api] POST ${endpoint}`);
  return apiRequest<T>(endpoint, { ...options, method: 'POST', body });
};

/**
 * Convenience method for PUT requests
 */
export const put = <T = any>(
  endpoint: string,
  body?: any,
  options?: Omit<RequestOptions, 'method' | 'body'>
): Promise<T> => {
  debugLog(`[DEBUG api] PUT ${endpoint}`);
  return apiRequest<T>(endpoint, { ...options, method: 'PUT', body });
};

/**
 * Convenience method for DELETE requests
 */
export const del = <T = any>(
  endpoint: string,
  options?: Omit<RequestOptions, 'method' | 'body'>
): Promise<T> => {
  debugLog(`[DEBUG api] DELETE ${endpoint}`);
  return apiRequest<T>(endpoint, { ...options, method: 'DELETE' });
};

/**
 * Convenience method for PATCH requests
 */
export const patch = <T = any>(
  endpoint: string,
  body?: any,
  options?: Omit<RequestOptions, 'method' | 'body'>
): Promise<T> => {
  debugLog(`[DEBUG api] PATCH ${endpoint}`);
  return apiRequest<T>(endpoint, { ...options, method: 'PATCH', body });
};

// Default export
export default {
  get,
  post,
  put,
  delete: del,
  patch,
};
